import {FrontendEvent} from "@core/model";
import {AppDispatch} from "@core/store";
import {Selectors} from "@core/store/selectors";
import {notify} from "@core/store/slices/notificationsSlice";
import {EventSourcePolyfill} from "event-source-polyfill";
import {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";

const useEventUserSource = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tenantId = useSelector(Selectors.tenantId);
  const userRole = useSelector(Selectors.userRole);
  const accessToken = useSelector(Selectors.accessToken);
  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);

  useEffect(() => {
    if (!userRole || !tenantId || !accessToken) {
      eventSourceRef.current?.close();
      return;
    }

    const createEventSource = (accessToken: string) => {
      const url = `${process.env.REACT_APP_API_URL}/api/event/stream/user`;
      const eventUser = new EventSourcePolyfill(url, {
        headers: {
          Authorization: "Bearer " + accessToken,
          ...(userRole === "ADMIN" ? {"X-Sw-Tenant-Id": tenantId} : {}),
        },
      });

      eventUser.onmessage = message => {
        const event = JSON.parse(message.data) as FrontendEvent;
        dispatch(notify(event));
      };

      eventUser.onerror = () => {
        eventUser.close();
        setTimeout(() => {
          eventSourceRef.current = createEventSource(accessToken);
        }, 5000);
      };

      return eventUser;
    };

    eventSourceRef.current = createEventSource(accessToken);

    return () => {
      eventSourceRef.current?.close();
    };
  }, [tenantId, userRole, accessToken]);
};

const useEventTenantSource = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tenantId = useSelector(Selectors.tenantId);
  const userRole = useSelector(Selectors.userRole);
  const accessToken = useSelector(Selectors.accessToken);
  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);

  useEffect(() => {
    if (!userRole || !tenantId || !accessToken) {
      eventSourceRef.current?.close();
      return;
    }

    const createEventSource = (accessToken: string) => {
      const url = `${process.env.REACT_APP_API_URL}/api/event/stream/tenant`;
      const eventTenant = new EventSourcePolyfill(url, {
        headers: {
          Authorization: "Bearer " + accessToken,
          ...(userRole === "ADMIN" ? {"X-Sw-Tenant-Id": tenantId} : {}),
        },
      });

      eventTenant.onmessage = message => {
        const event = JSON.parse(message.data) as FrontendEvent;
        dispatch(notify(event));
      };

      eventTenant.onerror = () => {
        eventTenant.close();
        setTimeout(() => {
          eventSourceRef.current = createEventSource(accessToken);
        }, 5000);
      };

      return eventTenant;
    };

    eventSourceRef.current = createEventSource(accessToken);

    return () => {
      eventSourceRef.current?.close();
    };
  }, [tenantId, userRole, accessToken]);
};

export {useEventUserSource, useEventTenantSource};
